import React, { memo, useCallback, useEffect, useReducer, useMemo, useState, useRef } from 'react';

import {
    Button,
    Icon,
    IconMultiple,
    Markdown,
    SearchBar,
    Spacer,
    useTheme,
} from '@Sage/hoi-poi-ui';
import { isEmptyObject, isEqual, removeAccents } from '@Sage/ssm-utils';
import { AddOns, SectionFilter, FiltersProvider } from '@Sage/ssm-web5';
import Fuse from 'fuse.js';
import { useSelector } from 'react-redux';

import AbsoluteEmpty from 'components/AbsoluteEmpty';
import { getAutomationsTemplates } from 'services/AutomationsService';
import { getNumberFilters, cleanFilters } from 'utils/filters';
import { getLiteral, getLiteralWithParameters } from 'utils/getLiteral';
import { stripDiacritics } from 'utils/strings';
import { logEvent } from 'utils/tracking';

import FeedbackImage from './components/AutomationTemplateCard/FeedbackImage';
import AutomationTemplateImage from './components/AutomationTemplateImage';
import './styles.scss';
import {
    getAutomationEntities,
    getAutomationEntityByValue,
    getAutomationEntityFilterOptions,
    getAutomationTriggerFilterOptions,
    normalizeAutomationEntityValue,
    resolveAutomationEntityIcon,
    TEMPLATE_ICONS,
} from './utils';

const initialState = {
    loading: true,
    error: false,
    rows: null,
    isFiltersOpen: false,
    numberFilters: 0,
    haveFilters: false,
    filters: null,
};

const fuseOptions = {
    threshold: 0.2,
    keys: ['title', 'description'],
    getFn: (obj, path) => {
        let value = Fuse.config.getFn(obj, path);
        if (Array.isArray(value)) {
            value = value.map((v) => getLiteral(v)).join(' ');
        } else {
            value = getLiteral(value);
        }
        return removeAccents(value?.trim());
    },
};

function reducer(state, action) {
    switch (action.type) {
        case 'load':
            return {
                ...state,
                loading: false,
                error: false,
                items: action.items,
            };
        case 'loading':
            return {
                ...state,
                loading: true,
                error: false,
                items: null,
            };
        case 'error':
            return {
                ...state,
                items: null,
                loading: false,
                error: true,
            };
        case 'setIsFiltersOpen':
            return {
                ...state,
                isFiltersOpen: action.value,
            };
        case 'setFilters':
            return {
                ...state,
                filters: action.filters,
                numberFilters: action.numberFilters,
                haveFilters: action.haveFilters,
            };
        default:
            throw new Error('No action provided');
    }
}

const AutomationsTemplates = memo(({ openBuilder, defaultEntity }) => {
    const [state, dispatch] = useReducer(reducer, initialState);
    const [searchInput, setSearchInput] = useState();
    const [quickFiltersKeys, setQuickFiltersKeys] = useState(['entity', 'action']);
    const searchInputRef = useRef(null);
    const cachedData = useRef({});
    const debounce = useRef(null);
    const filters = useRef({});
    const theme = useTheme();
    const marketingCampaignsEnabled = useSelector(
        (state) => state?.config?.userData?.marketingCampaigns,
    );
    const automationEntities = useMemo(
        () => getAutomationEntities(marketingCampaignsEnabled),
        [marketingCampaignsEnabled],
    );
    const normalizedDefaultEntity = useMemo(
        () => (defaultEntity ? normalizeAutomationEntityValue(defaultEntity) : defaultEntity),
        [defaultEntity],
    );

    const applyFilters = useCallback((data) => {
        let filteredData = data || [];

        if (filters.current.entity) {
            filteredData = filteredData.filter(
                (item) =>
                    normalizeAutomationEntityValue(item.triggerEntity) === filters.current.entity,
            );
        }

        if (filters.current.action) {
            filteredData = filteredData.filter((item) => {
                if (filters.current.action === 'create') {
                    return (
                        item.triggerEvent === filters.current.action ||
                        item.scene?.parameters?.action?.canCreate === true
                    );
                }
                return item.triggerEvent === filters.current.action;
            });
        }

        return filteredData;
    }, []);

    useEffect(() => {
        logEvent({
            event: defaultEntity || 'settings',
            submodule: 'automationsTemplate',
            functionality: 'listView',
        });
    }, [defaultEntity]);

    const get = useCallback(() => {
        getAutomationsTemplates()
            .then((data) => {
                cachedData.current = data || [];
                const filteredData = applyFilters(data);

                // Apply search filter
                if (searchInputRef.current) {
                    const rawText = stripDiacritics(searchInputRef.current?.toLowerCase()?.trim());
                    const fuse = new Fuse(filteredData, fuseOptions);
                    const matchingOptions = fuse.search(rawText).map((result) => result.item);
                    dispatch({ type: 'load', items: matchingOptions });
                } else {
                    dispatch({ type: 'load', items: filteredData });
                }
            })
            .catch((e) => {
                console.error(e);
                dispatch({ type: 'error' });
            });
    }, [applyFilters]);

    const load = useCallback(() => {
        dispatch({ type: 'loading' });
        get();
    }, [get]);

    useEffect(() => {
        // Preload default entity filter when provided (from entity menus)
        if (normalizedDefaultEntity && !filters.current.entity) {
            filters.current = { ...filters.current, entity: normalizedDefaultEntity };

            // Reflect default entity in the SectionFilter UI values/state
            const selectedEntity = automationEntities.find(
                (e) => e.trueName === normalizedDefaultEntity,
            );
            const values = {
                entity: {
                    value: normalizedDefaultEntity,
                    label: getLiteral(selectedEntity?.labels?.singular),
                },
            };
            dispatch({
                type: 'setFilters',
                numberFilters: 1,
                haveFilters: true,
                filters: values,
            });
        }
        load();
    }, [automationEntities, load, normalizedDefaultEntity]);

    const searchByText = useCallback(
        (text) => {
            clearTimeout(debounce.current);
            debounce.current = setTimeout(() => {
                const filteredData = applyFilters(cachedData.current);

                // Then apply search
                if (!text) {
                    dispatch({ type: 'load', items: filteredData });
                } else {
                    const rawText = stripDiacritics(text?.toLowerCase()?.trim());
                    const fuse = new Fuse(filteredData, fuseOptions);
                    const matchingOptions = fuse.search(rawText).map((result) => result.item);
                    dispatch({ type: 'load', items: matchingOptions });
                }

                logEvent({
                    event: defaultEntity || 'settings',
                    submodule: 'automationsTemplate',
                    functionality: 'search',
                });
            }, 250);
        },
        [applyFilters, defaultEntity],
    );

    const onChangeSearch = useCallback(
        (e) => {
            searchInputRef.current = e?.target?.value || '';
            setSearchInput(e?.target?.value || '');
            if (!e?.target?.value) searchByText();
        },
        [searchByText],
    );

    const onBlurSearch = useCallback(
        (text) => {
            searchByText(text);
        },
        [searchByText],
    );

    const onUseTemplate = useCallback(
        (d) => {
            openBuilder(undefined, { scene: d.scene, defaultEntity });
            logEvent({
                event: defaultEntity || 'settings',
                submodule: 'automationsTemplate',
                functionality: 'createTemplateStart',
            });
        },
        [openBuilder, defaultEntity],
    );

    const onChangeFilters = useCallback(
        (values) => {
            const numberFilters = getNumberFilters(values || {});
            dispatch({
                type: 'setFilters',
                numberFilters,
                haveFilters: numberFilters > 0,
                filters: values,
            });

            const newFilters = cleanFilters({
                entity: values?.entity?.value,
                action: values?.action?.value,
            });

            if (
                isEqual(newFilters, filters.current) ||
                (isEmptyObject(values) && isEmptyObject(filters.current))
            ) {
                filters.current = newFilters;
            } else {
                filters.current = newFilters;
                searchByText(searchInputRef.current);
            }

            logEvent({
                event: defaultEntity || 'settings',
                submodule: 'automationsTemplate',
                functionality: 'filter',
            });
        },
        [searchByText, defaultEntity],
    );

    const openFilters = useCallback(() => {
        dispatch({
            type: 'setIsFiltersOpen',
            value: true,
        });
    }, []);

    const onCloseFilters = useCallback(
        () =>
            dispatch({
                type: 'setIsFiltersOpen',
                value: false,
            }),
        [],
    );

    const toggleQuickFilter = useCallback(
        (id) => {
            const alreadyExists = quickFiltersKeys.includes(id);
            const newQuickFilters = alreadyExists
                ? quickFiltersKeys.filter((i) => i !== id)
                : [...quickFiltersKeys, id];

            setQuickFiltersKeys(newQuickFilters);
        },
        [quickFiltersKeys],
    );

    const onReOrder = useCallback(
        (startIndex, endIndex) => {
            const result = [...quickFiltersKeys];
            const [removed] = result.splice(startIndex, 1);
            result.splice(endIndex, 0, removed);
            setQuickFiltersKeys(result);
        },
        [quickFiltersKeys],
    );

    const filterSchema = useMemo(() => {
        const schema = [
            {
                label: getLiteral('label_triggering_event'),
                id: 'action',
                fields: [
                    {
                        id: 'action',
                        dataType: 'singleValueList',
                        inputProps: {
                            placeholder: getLiteral('cfm_placeholder_search'),
                            options: getAutomationTriggerFilterOptions(marketingCampaignsEnabled),
                        },
                    },
                ],
            },
            {
                label: getLiteral('label_triggering_entity'),
                id: 'entity',
                fields: [
                    {
                        id: 'entity',
                        dataType: 'singleValueList',
                        inputProps: {
                            placeholder: getLiteral('cfm_placeholder_search'),
                            options: getAutomationEntityFilterOptions(marketingCampaignsEnabled),
                            defaultValue: normalizedDefaultEntity
                                ? {
                                      label: getLiteral(
                                          automationEntities.find(
                                              (e) => e.trueName === normalizedDefaultEntity,
                                          )?.labels?.singular,
                                      ),
                                      value: normalizedDefaultEntity,
                                  }
                                : undefined,
                        },
                    },
                ],
            },
        ];
        return schema;
    }, [automationEntities, marketingCampaignsEnabled, normalizedDefaultEntity]);

    const quickFilterSchema = useMemo(() => {
        return filterSchema.filter((filter) => quickFiltersKeys.includes(filter.id));
    }, [filterSchema, quickFiltersKeys]);

    const generalFilterSchema = useMemo(() => {
        return filterSchema.filter((filter) => !quickFiltersKeys.includes(filter.id));
    }, [filterSchema, quickFiltersKeys]);

    const clearFilters = useCallback(() => {
        onChangeFilters(null);
    }, [onChangeFilters]);

    const getDescription = useCallback(
        (cardData) => {
            if (!cardData) {
                return (
                    <Markdown
                        content={getLiteral('label_automations_send_us_feedback_card_desc')}
                        key="description-feedback"
                    />
                );
            }

            return (
                <>
                    {cardData?.description[0] ? (
                        <Markdown
                            content={getLiteral(cardData.description[0])}
                            key="description-0"
                        />
                    ) : null}

                    {cardData?.description[0] ? (
                        <Icon
                            name="arrowRight"
                            color={theme.colors.semantic.positive500}
                            key="arrow-right"
                        />
                    ) : null}

                    {cardData?.description[1] ? (
                        <Markdown
                            content={getLiteral(cardData.description[1])}
                            key="description-1"
                        />
                    ) : null}
                </>
            );
        },
        [theme.colors.semantic.positive500],
    );

    const transformToAddOnsSchema = useMemo(() => {
        const items = state.items;
        if (!items?.length) return [];

        const templatesSchema = items.map((item) => ({
            title: getLiteral(item.title),
            icon: resolveAutomationEntityIcon(item.triggerEntity),
            content: {
                description: getDescription(item),
            },
            figure: {
                img: () => (
                    <div className="fm-automations-settings__template__image">
                        <AutomationTemplateImage
                            firstIcon={
                                <IconMultiple
                                    {...TEMPLATE_ICONS[item.iconTrigger]}
                                    firstProps={{
                                        color: TEMPLATE_ICONS[item.iconTrigger]?.firstColor?.(
                                            theme,
                                        ),
                                    }}
                                    secondProps={{
                                        color: TEMPLATE_ICONS[item.iconTrigger]?.secondColor?.(
                                            theme,
                                        ),
                                    }}
                                />
                            }
                            secondIcon={
                                <IconMultiple
                                    {...TEMPLATE_ICONS[item.iconAction]}
                                    firstProps={{
                                        color: TEMPLATE_ICONS[item.iconAction]?.firstColor?.(theme),
                                    }}
                                    secondProps={{
                                        color: TEMPLATE_ICONS[item.iconAction]?.secondColor?.(
                                            theme,
                                        ),
                                    }}
                                />
                            }
                        />
                    </div>
                ),
            },
            info: {
                texts: getLiteral(item.descriptionHover),
                cta: [
                    {
                        type: 'primary',
                        text: getLiteral('action_use_template'),
                        onClick: () => onUseTemplate(item),
                    },
                ],
            },
        }));

        const feedbackCard = {
            title: getLiteral('label_automations_send_us_feedback_card'),
            icon: 'helpCenterFilled',
            content: {
                description: getDescription(),
            },
            figure: {
                img: () => <FeedbackImage />,
            },
            info: {
                texts: getLiteral('label_automations_send_us_feedback_card_hover'),
                cta: [
                    {
                        type: 'primary',
                        text: getLiteral('action_send_us_feedback'),
                        onClick: () => {
                            window.open(getLiteral('label_send_us_feedback_form'), '_blank');
                        },
                    },
                ],
            },
        };

        return [...templatesSchema, feedbackCard];
    }, [getDescription, onUseTemplate, theme, state.items]);

    const onCardHover = useCallback(
        (isHovered, cardData) => {
            if (isHovered) {
                logEvent({
                    event: defaultEntity || 'settings',
                    submodule: 'automationsTemplate',
                    functionality: 'templateHover',
                    data: { templateId: cardData?.title },
                });
            }
        },
        [defaultEntity],
    );

    const linkCallback = useCallback((link) => {
        if (!link) return;
        window.open(link, '_blank');
    }, []);

    const templates = useMemo(() => {
        if (state.items?.length <= 0) return;
        return (
            <AddOns
                schema={transformToAddOnsSchema}
                onHover={onCardHover}
                linkCallback={linkCallback}
            />
        );
    }, [state.items, transformToAddOnsSchema, onCardHover, linkCallback]);

    const haveFilters = state.haveFilters;
    const showEmptyView = !state.loading && !state.error && !state.items?.length;

    const handleCreate = useCallback(() => {
        openBuilder();
    }, [openBuilder]);

    return (
        <FiltersProvider>
            <div className="fm-automations-settings__toolbar">
                <SearchBar
                    className="fm-automation-settings__search-bar"
                    placeholder={getLiteral('action_search_automation_templates')}
                    isFullWidth
                    inputValue={searchInput}
                    onChange={onChangeSearch}
                    onBlurSearch={onBlurSearch}
                    forceBlurOnEnter
                    useAsSimpleSearch
                />
                <SectionFilter
                    useQuickFilters
                    quickFilters={quickFiltersKeys}
                    schema={filterSchema}
                    onChange={onChangeFilters}
                    values={state.filters}
                    noFiltersProvider={true}
                />
                {!haveFilters && (
                    <Button type="terciary" size="medium" icon="filterList" onClick={openFilters}>
                        {getLiteral('action_filters')}
                    </Button>
                )}
                {haveFilters && (
                    <Button
                        type="secondary"
                        size="medium"
                        icon="filterListActive"
                        onClick={openFilters}
                    >
                        {getLiteralWithParameters('label_filters_button', [state.numberFilters])}
                    </Button>
                )}
                <Button onClick={handleCreate}>{getLiteral('action_create')}</Button>
            </div>
            <Spacer y={2} />
            {templates}
            {showEmptyView && (
                <AbsoluteEmpty
                    title={getLiteral('error_notresultfound')}
                    subtitle={getLiteral('helptext_search_filter_text')}
                    actions={[
                        <Button key="clear-filters" type="secondary" onClick={clearFilters}>
                            {getLiteral('action_clear_filters')}
                        </Button>,
                    ]}
                />
            )}
            <SectionFilter
                isOpen={state.isFiltersOpen}
                onClose={onCloseFilters}
                id="fm-automations-templates__filter-section"
                quickSchema={quickFilterSchema}
                schema={generalFilterSchema}
                onChange={onChangeFilters}
                values={state.filters}
                toggleQuickFilter={toggleQuickFilter}
                onReOrder={onReOrder}
                isPinnable={true}
                noFiltersProvider={true}
            />
        </FiltersProvider>
    );
});

export default AutomationsTemplates;
